import { useAuth, useCan } from '@/lib/auth'
import { formatLocalDateTime } from '@/lib/format'
import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'
import { t } from '@/lib/i18n'

const UserRow = z.object({
  id: z.string(),
  email: z.string(),
  display_name: z.string(),
  role: z.enum(['admin', 'viewer']),
  must_change_password: z.boolean(),
  created_at: z.string(),
  last_login_at: z.string().nullable(),
  sites: z.array(z.object({ id: z.string(), name: z.string() })),
  can_chat: z.boolean(),
  chat_tools: z.array(z.string()),
})
type UserRow = z.infer<typeof UserRow>

const UsersResponse = z.object({ users: z.array(UserRow) })

export const Route = createFileRoute('/users')({
  component: UsersPage,
})

function UsersPage() {
  const canManage = useCan('user.manage')
  const { status } = useAuth()
  const [openId, setOpenId] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['users'],
    enabled: canManage && status === 'authed',
    queryFn: async () => {
      const r = await fetch('/api/users', { headers: { Accept: 'application/json' } })
      if (!r.ok) throw new Error(t('Failed to load users ({status})', { status: r.status }))
      const body = await r.json()
      return UsersResponse.parse(body)
    },
  })

  if (status !== 'authed') return null
  if (!canManage) {
    return (
      <div className="text-sm text-foreground-muted">
        {t("You don't have access to the user manager.")}{' '}
        <Link to="/" className="text-primary">
          {t('Back to portfolio')}
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">{t('Users')}</h1>
        <p className="text-sm text-foreground-muted">
          {t('Display-only panel. To create, edit, delete, reset passwords or change site access, ask the chatbot — it can call the user-management tools for you.')}
        </p>
      </div>

      {isLoading && <p className="text-sm text-foreground-muted">{t('Loading…')}</p>}
      {error && (
        <p className="text-sm text-critical" role="alert">
          {(error as Error).message}
        </p>
      )}

      {data && (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-card text-foreground-muted">
              <tr>
                <th className="w-8" />
                <th className="text-left px-3 py-2 font-medium">{t('Email')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('Name')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('Role')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('Chat')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('Sites')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('Last login')}</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <UserRowView
                  key={u.id}
                  user={u}
                  expanded={openId === u.id}
                  onToggle={() => setOpenId(openId === u.id ? null : u.id)}
                />
              ))}
              {data.users.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-center text-foreground-muted">
                    {t('No users.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function UserRowView({
  user,
  expanded,
  onToggle,
}: {
  user: UserRow
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr
        className="border-t border-border cursor-pointer hover:bg-accent/50"
        onClick={onToggle}
      >
        <td className="px-2 py-2 text-foreground-muted">
          {expanded ? (
            <ChevronDown className="w-4 h-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          )}
        </td>
        <td className="px-3 py-2 font-mono text-xs">{user.email}</td>
        <td className="px-3 py-2">{user.display_name}</td>
        <td className="px-3 py-2">
          <span
            className={
              user.role === 'admin'
                ? 'inline-flex items-center rounded px-1.5 py-0.5 text-xs bg-primary/10 text-primary'
                : 'inline-flex items-center rounded px-1.5 py-0.5 text-xs bg-accent text-foreground-muted'
            }
          >
            {user.role}
          </span>
        </td>
        <td className="px-3 py-2">
          {user.can_chat ? (
            <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs bg-primary/10 text-primary">
              {t('yes · {n} tools', { n: user.chat_tools.length })}
            </span>
          ) : (
            <span className="text-foreground-muted text-xs">{t('no')}</span>
          )}
        </td>
        <td className="px-3 py-2 text-foreground-muted">
          {user.role === 'admin'
            ? t('All sites')
            : user.sites.length === 0
              ? <span className="italic">{t('None assigned')}</span>
              : user.sites.map((s) => s.name).join(', ')}
        </td>
        <td className="px-3 py-2 text-foreground-muted tabular-nums">
          {user.last_login_at ? formatLocalDateTime(user.last_login_at) : <span className="italic">{t('never')}</span>}
        </td>
      </tr>
      {expanded && <UserDetail user={user} />}
    </>
  )
}

function UserDetail({ user }: { user: UserRow }) {
  const reads = user.chat_tools.filter((t) => t.startsWith('list_') || t.startsWith('get_'))
  const writes = user.chat_tools.filter((t) => !(t.startsWith('list_') || t.startsWith('get_')))
  return (
    <tr className="border-t border-border bg-card">
      <td />
      <td colSpan={6} className="px-3 py-3">
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
          <div>
            <dt className="text-foreground-muted text-xs uppercase tracking-wide mb-1">
              {t('Sites')}
            </dt>
            <dd>
              {user.role === 'admin' ? (
                <span className="text-foreground-muted">{t('All sites (admin)')}</span>
              ) : user.sites.length === 0 ? (
                <span className="text-foreground-muted italic">{t('None assigned')}</span>
              ) : (
                <ul className="space-y-0.5">
                  {user.sites.map((s) => (
                    <li key={s.id} className="tabular-nums">
                      <span className="font-mono text-xs text-foreground-muted">{s.id}</span>
                      {' — '}
                      {s.name}
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>

          <div>
            <dt className="text-foreground-muted text-xs uppercase tracking-wide mb-1">
              {t('Chat access')}
            </dt>
            <dd>
              {!user.can_chat ? (
                <span className="text-foreground-muted">{t('Chatbot disabled')}</span>
              ) : (
                <div className="space-y-2">
                  {reads.length > 0 && (
                    <div>
                      <div className="text-xs text-foreground-muted mb-0.5">
                        {t('Read tools ({n})', { n: reads.length })}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {reads.map((t) => (
                          <code
                            key={t}
                            className="px-1.5 py-0.5 rounded bg-accent text-foreground-muted text-xs"
                          >
                            {t}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}
                  {writes.length > 0 && (
                    <div>
                      <div className="text-xs text-foreground-muted mb-0.5">
                        {t('Write tools ({n})', { n: writes.length })}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {writes.map((t) => (
                          <code
                            key={t}
                            className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-xs"
                          >
                            {t}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}
                  {reads.length === 0 && writes.length === 0 && (
                    <span className="text-foreground-muted">{t('No tools granted')}</span>
                  )}
                </div>
              )}
            </dd>
          </div>

          <div>
            <dt className="text-foreground-muted text-xs uppercase tracking-wide mb-1">
              {t('Created')}
            </dt>
            <dd className="tabular-nums text-foreground-muted">{formatLocalDateTime(user.created_at)}</dd>
          </div>

          <div>
            <dt className="text-foreground-muted text-xs uppercase tracking-wide mb-1">
              {t('Flags')}
            </dt>
            <dd className="text-foreground-muted text-xs">
              {user.must_change_password
                ? t('Must change password on next login')
                : t('Password rotation not required')}
            </dd>
          </div>
        </dl>
      </td>
    </tr>
  )
}
